/**
 * Решения окна продажи по ответам кассового агента — чистые функции, без сети и без React.
 *
 * `sellGate` отвечает на один вопрос: можно ли сейчас пробивать чек, а если нет — почему и что делать кассиру. Правило честности:
 * сомнение в ТЕХНИКЕ закрывает продажу. Продажа открыта только когда агент на связи и принял токен, аппаратура не занята прошлой
 * операцией (`409 device-busy`), касса сама сказала `canSell: true` и среди её неполадок нет ни одной с `block`.
 * **Долг продажу не закрывает** (решение владельца 18.10.2026): неразобранная («деньги взяты, чека нет») или отложенная запись
 * кассу не блокирует, «Отложить» — норма; от второго чека поверх неизвестного защищает сам агент занятостью аппаратуры.
 *
 * `verdictOf` превращает итог чека в три честных слова: «продано», «не продано» (можно исправить и повторить с НОВЫМ ключом) и
 * «неизвестно» (чек не повторять, пока не выяснено). Деньги по карте учитываются отдельно: списанные и не возвращённые — это
 * «неизвестно» с ручной отменой, что бы ни написал статус.
 */

import type { FiscalSupport } from './agentCapabilities'
import type { DeviceProblem, DeviceSnapshot, FiscalCard, FiscalDocument, FiscalError, FiscalParking, FiscalResult } from './fiscalAgent'

/** Фаза связи с агентом (та же шкала, что у окна «Оборудование»). */
export type AgentLinkPhase = 'searching' | 'connected' | 'lost' | 'missing' | 'choose' | 'outdated' | 'denied'

export type SaleBlockReason =
  | 'searching'
  | 'missing'
  | 'lost'
  | 'choose'
  | 'outdated'
  | 'denied'
  | 'fiscal-unsupported'
  | 'workplace-onec'
  | 'token-refused'
  | 'agent-busy'
  | 'settling'
  | 'busy'
  | 'checking'
  | 'device-unreachable'
  | 'device-blocked'
  | 'shift-closed'
  | 'shift-expired'

/** Что кассир может сделать, чтобы продажа открылась; `null` — только ждать или обращаться к администратору. */
export type SaleGateAction =
  | 'install-agent'
  | 'update-agent'
  | 'choose-agent'
  | 'allow-site'
  | 'retry'
  | 'open-shift'
  | 'close-shift'

export interface SaleGateInput {
  link: AgentLinkPhase
  /** Умеет ли агент фискальные маршруты и не в режиме ли он «1С» (`fiscalSupportOf`); нет — как «умеет». */
  fiscalSupport?: FiscalSupport
  /** Агент не принял токен даже после обновления (401 дважды). */
  tokenRefused: boolean
  /** Последний снимок кассы; `null` — ещё не читали. */
  device: DeviceSnapshot | null
  /** Последнее чтение кассы не удалось (агент не ответил или отказал): прежний снимок устарел и не годится. */
  deviceUnreachable: boolean
  /** Слова агента, почему кассу прочитать нельзя (например «Касса в агенте не настроена»). */
  deviceNote?: string | null
  /**
   * Техническая занятость аппаратуры: агент ответил `409 device-busy` (терминал или касса ещё работают с прошлой операцией).
   * Секунды до повтора считает вызывающий по часам; `null` — не занято. Это единственное, что остаётся от прежнего блока по долгу.
   */
  agentBusy?: { retryAfterSeconds: number; device: string | null; text: string } | null
  /** Исход только что отправленной команды не выяснен (оборвалась связь): ждём ответа агента по ключу. Не долг, а секунды ожидания. */
  settling?: boolean
  /** Идёт чек, смена или наличные. */
  busy: boolean
}

export interface SaleGate {
  open: boolean
  reason: SaleBlockReason | null
  /** Почему закрыто, словами; для неполадок кассы — слова агента дословно (ADR-8). */
  text: string
  action: SaleGateAction | null
  /** Совет агента по неполадке (`problems[].action`), если он есть. */
  advice: string | null
  /** Неполадки с `block`, из-за которых продавать нельзя. */
  blocking: readonly DeviceProblem[]
  /** Предупреждения: продавать можно, но кассиру нужно знать (смена скоро истечёт, чеки не ушли в ОФД). */
  warnings: readonly DeviceProblem[]
}

const closed = (reason: SaleBlockReason, text: string, action: SaleGateAction | null, extra: Partial<SaleGate> = {}): SaleGate => ({
  open: false,
  reason,
  text,
  action,
  advice: null,
  blocking: [],
  warnings: [],
  ...extra,
})

const LINK_TEXTS: Readonly<Record<Exclude<AgentLinkPhase, 'connected'>, { text: string; action: SaleGateAction | null }>> = {
  searching: { text: 'Ищем кассовый агент на этом компьютере…', action: null },
  missing: { text: 'Кассовый агент не найден на этом компьютере. Без него продавать нельзя.', action: 'install-agent' },
  lost: { text: 'Связь с кассовым агентом потеряна. Продажа закрыта, пока он не вернётся.', action: 'retry' },
  choose: { text: 'Найдено несколько кассовых агентов — выберите, с каким работать.', action: 'choose-agent' },
  outdated: { text: 'Кассовый агент устарел: обновите его.', action: 'update-agent' },
  denied: { text: 'Кассовый агент не пускает этот сайт: добавьте адрес сайта в его настройки.', action: 'allow-site' },
}

export function sellGate(input: SaleGateInput): SaleGate {
  if (input.link !== 'connected') {
    const word = LINK_TEXTS[input.link]
    return closed(input.link, word.text, word.action)
  }
  if (input.fiscalSupport?.kind === 'outdated') return closed('fiscal-unsupported', input.fiscalSupport.text, 'update-agent')
  if (input.fiscalSupport?.kind === 'onec-mode') return closed('workplace-onec', input.fiscalSupport.text, null)
  if (input.tokenRefused) {
    return closed('token-refused', 'Агент не принял токен рабочего места. Войдите заново или обратитесь к администратору.', 'retry')
  }
  if (input.agentBusy != null) {
    const seconds = Math.max(1, Math.ceil(input.agentBusy.retryAfterSeconds))
    return closed('agent-busy', input.agentBusy.text, 'retry', { advice: `Повторите через ${seconds} с.` })
  }
  if (input.settling === true) return closed('settling', 'Выясняем у агента исход прошлой команды…', null)
  if (input.busy) return closed('busy', 'На кассе идёт операция. Дождитесь её окончания.', null)

  // Не удалось прочитать кассу — прежний снимок устарел: по нему продавать нельзя, даже если он был «можно».
  if (input.deviceUnreachable) {
    return closed('device-unreachable', input.deviceNote ?? 'Не удалось прочитать состояние кассы через агент.', 'retry')
  }
  const device = input.device
  if (device === null) return closed('checking', 'Проверяем кассу…', null)
  const blocking = device.problems.filter((problem) => problem.severity === 'block')
  const warnings = device.problems.filter((problem) => problem.severity === 'warn')
  if (device.connection !== 'ok') {
    const first = blocking[0]
    return closed('device-unreachable', first?.text ?? 'Нет связи с кассой.', 'retry', { advice: first?.action ?? null, blocking, warnings })
  }
  if (device.canSell && blocking.length === 0) {
    return { open: true, reason: null, text: '', action: null, advice: null, blocking: [], warnings }
  }

  // Смена — единственное, что мешает: тогда её можно открыть кнопкой. Если мешает ещё что-то (нет бумаги), открытие смены не поможет.
  const only = blocking.length === 1 ? blocking[0] : undefined
  if (only?.code === 'shift-closed') {
    return closed('shift-closed', only.text, 'open-shift', { advice: only.action, blocking, warnings })
  }
  if (only?.code === 'shift-expired') {
    return closed('shift-expired', only.text, 'close-shift', { advice: only.action, blocking, warnings })
  }
  const first = blocking[0]
  return closed('device-blocked', first?.text ?? 'Касса не готова к продаже.', first === undefined ? 'retry' : null, {
    advice: first?.action ?? null,
    blocking,
    warnings,
  })
}

// ---- итог чека ----

/** Деньги по карте в итоге операции, как их надо понимать кассиру. */
export interface CardOutcomeView {
  outcome: string
  rrn: string | null
  maskedPan: string | null
  authCode: string | null
  /** Слип терминала: печатается и выдаётся клиенту. */
  receiptText: string | null
  /** Деньги списаны и не возвращены: отменить на терминале вручную по `rrn`. */
  needsManualVoid: boolean
  /** Платёж отменён по слову человека (автоотмены нет). */
  voided: boolean
}

/** Что агент добавил к итогу сверх «продано / не продано / неизвестно»: ключ операции и слова для кассира. Нет — итог собран окном (обрыв связи). */
export interface VerdictMeta {
  operationId: string
  /** Предупреждение агента дословно («пробит, но не напечатан», «это фискальный документ, отменить нельзя»…). */
  notice: string | null
  /** После повтора в накопителе больше одного документа: возможно, пробито два чека. */
  possibleDoubleReceipt: boolean
  /** Операция отложена. */
  parking: FiscalParking | null
  /** Агент сам сказал, можно ли «Повторить проведение» (режим 1С); `null` — не сказал. */
  canRepunch: boolean | null
  /** Номер попытки чека. */
  attempt: number | null
  /** `false` — пробит, но не напечатан: можно допечатать. */
  printed: boolean | null
  /** Факты отправки и ответа; ответ сам по себе не означает успех. */
  stage?: FiscalResult['stage']
  oneCStage?: FiscalResult['oneCStage']
}

export type SaleVerdict =
  /** Чек пробит. */
  | { kind: 'sold'; fiscal: FiscalDocument | null; shiftAutoOpened: boolean; card: CardOutcomeView | null; cashSum: number | null; meta?: VerdictMeta }
  /** Чек не пробит наверняка: исправить и повторить с НОВЫМ ключом операции. */
  | { kind: 'not-sold'; error: FiscalError | null; card: CardOutcomeView | null; meta?: VerdictMeta }
  /** Неизвестно: чек не повторять, выяснить через `recover`. */
  | { kind: 'unknown'; error: FiscalError | null; card: CardOutcomeView | null; meta?: VerdictMeta }

/** Статусы терминала, при которых деньги у банка могли быть списаны. */
const MONEY_MAY_BE_TAKEN = new Set(['approved', 'unknown'])

function cardView(card: FiscalCard | null, status: FiscalResult['status']): CardOutcomeView | null {
  if (card === null) return null
  // Списано (или неясно) и чек не вышел, а платёж не отменён — это ручная отмена, даже если агент о флаге промолчал.
  const taken = MONEY_MAY_BE_TAKEN.has(card.outcome) && !card.voided && status !== 'done'
  return {
    outcome: card.outcome,
    rrn: card.rrn,
    maskedPan: card.maskedPan,
    authCode: card.authCode,
    receiptText: card.receiptText,
    needsManualVoid: card.needsManualVoid || taken,
    voided: card.voided,
  }
}

export function verdictOf(result: FiscalResult): SaleVerdict {
  const card = cardView(result.card, result.status)
  const meta: VerdictMeta = {
    operationId: result.operationId,
    notice: result.notice,
    possibleDoubleReceipt: result.possibleDoubleReceipt,
    parking: result.parking,
    canRepunch: result.canRepunch,
    attempt: result.attempt,
    printed: result.fiscal?.printed ?? null,
    stage: result.stage,
    oneCStage: result.oneCStage,
  }
  if (result.status === 'done') {
    return { kind: 'sold', fiscal: result.fiscal, shiftAutoOpened: result.shiftAutoOpened, card, cashSum: result.cashSum, meta }
  }
  // Отказ, при котором деньги по карте могли уйти, а возврата нет, — не «не продано», а «неизвестно»: решает человек.
  if (result.status === 'in-doubt' || card?.needsManualVoid === true) {
    return { kind: 'unknown', error: result.error, card, meta }
  }
  return { kind: 'not-sold', error: result.error, card, meta }
}
